import { Link } from 'react-router'
import { Section, SectionWrapper } from '@/components/layout/Section'
import { Badge } from '@/components/ui/badge'
import { POSTS } from '@/content/docs.generated'
import { formatDocDate } from '@/lib/date'

/**
 * `.relatedpostswrapper` — 51 cards across 17 posts, three per post.
 *
 * Webflow's CMS picked these by collection order; here a post's `related`
 * slugs win when set, otherwise they prefer the same category, then fall back
 * to most recent, so the three are always filled.
 */
export function RelatedPosts({
  currentSlug,
  category,
  slugs,
}: {
  currentSlug: string
  category: string | null
  slugs?: string[]
}) {
  const others = POSTS.filter((p) => p.slug !== currentSlug)
  const picked = (slugs ?? []).flatMap((s) => others.filter((p) => p.slug === s))
  const sameCategory = others.filter((p) => p.category === category)
  const related = picked.length
    ? picked
    : [...sameCategory, ...others.filter((p) => p.category !== category)].slice(0, 3)

  if (!related.length) return null

  return (
    <Section padding="lg" surface="gray">
      <SectionWrapper layout="stack" gap="gap">
        <h5 className="m-0 font-secondary text-small tracking-label text-font-default uppercase">
          Keep Reading
        </h5>
        <div className="grid w-full grid-cols-1 gap-6 md:grid-cols-3">
          {related.map((p) => (
            <Link
              key={p.slug}
              to={p.route}
              className="group flex flex-col overflow-hidden rounded-lg border border-solid border-dark-blue-50 bg-white no-underline transition-shadow hover:shadow-card"
            >
              {p.heroImage && (
                <img
                  src={p.heroImage}
                  alt=""
                  loading="lazy"
                  className="h-[204px] w-full object-cover"
                />
              )}
              <div className="flex flex-1 flex-col gap-3 p-6">
                {p.category && <Badge>{p.category}</Badge>}
                <span className="font-heading text-h6 leading-tight text-grayscale-900 group-hover:text-blue">
                  {p.title}
                </span>
                {p.date && (
                  <span className="mt-auto font-mono text-xs text-font-secondary">
                    {formatDocDate(p.date)}
                  </span>
                )}
              </div>
            </Link>
          ))}
        </div>
      </SectionWrapper>
    </Section>
  )
}
